import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useAuthStore } from '@/store/auth-store'
import { useNotificationStore } from '@/store/notification-store'
import { UserIcon, LogOutIcon, BellIcon, InboxIcon, ChevronRightIcon } from '@/shared/components/icons'
import { AccentStrip } from '@/shared/components/ui'
import { cn } from '@/shared/lib/utils'

const PREVIEW_COUNT = 4

// The account entry point in the header. It always carries the profile and sign-out
// actions. On mobile it also folds in a notifications preview with the unread count on the
// avatar, because the mobile header has no room for a separate bell. From lg up the desktop
// NotificationBell owns notifications, so this menu drops them and shows account only.
export function AccountMenu({
  fullName,
  pictureUrl,
}: {
  fullName: string
  pictureUrl: string | null
}) {
  const navigate = useNavigate()
  const logout = useAuthStore((s) => s.logout)
  const notifications = useNotificationStore((s) => s.notifications)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const markRead = useNotificationStore((s) => s.markRead)

  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const preview = notifications.slice(0, PREVIEW_COUNT)

  const openNotification = (id: string, linkPath: string | null) => {
    setOpen(false)
    void markRead(id)
    navigate(linkPath ?? '/notifications')
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-full transition-shadow',
          open ? 'ring-2 ring-brand-200' : 'hover:ring-2 hover:ring-neutral-200'
        )}
      >
        {pictureUrl ? (
          <img src={pictureUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy-900 text-xs font-bold text-white">
            {initialsOf(fullName)}
          </span>
        )}
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-600 px-1 text-[10px] font-bold text-white ring-2 ring-white lg:hidden">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-full z-50 mt-2 w-[20rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-line bg-white shadow-float"
        >
          {/* Identity */}
          <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
            {pictureUrl ? (
              <img src={pictureUrl} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy-900 text-xs font-bold text-white">
                {initialsOf(fullName)}
              </span>
            )}
            <p className="min-w-0 truncate text-sm font-semibold text-navy-900">{fullName}</p>
          </div>

          {/* Notifications, folded in on mobile only. On desktop the header bell owns these. */}
          <div className="border-b border-line lg:hidden">
            <div className="flex items-center justify-between px-4 pb-1.5 pt-3">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-neutral-500">
                <BellIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Notifications
              </span>
              {unreadCount > 0 && (
                <span className="text-xs font-semibold text-brand-600">{unreadCount} unread</span>
              )}
            </div>

            {preview.length === 0 ? (
              <div className="px-4 py-5 text-center">
                <span className="mx-auto mb-1.5 flex h-8 w-8 items-center justify-center text-neutral-400">
                  <InboxIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <p className="text-xs text-neutral-500">
                  Updates on your enquiries and payouts will appear here.
                </p>
              </div>
            ) : (
              <ul className="max-h-64 overflow-y-auto">
                {preview.map((n) => {
                  const unread = !n.read_at
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => openNotification(n.id, n.link_path)}
                        className="group relative block w-full py-2.5 pl-5 pr-4 text-left transition-colors hover:bg-neutral-50"
                      >
                        {unread && <AccentStrip tone="brand" />}
                        <p className={cn('text-sm text-navy-900', unread ? 'font-bold' : 'font-medium')}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-neutral-600">
                            {n.body}
                          </p>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            <Link
              to="/notifications"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-neutral-50"
            >
              View all notifications
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          {/* Account actions */}
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-navy-900 transition-colors hover:bg-neutral-50"
          >
            <UserIcon className="h-4 w-4 text-neutral-500" aria-hidden="true" />
            My profile
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void logout()
            }}
            className="flex w-full items-center gap-3 border-t border-line px-4 py-3 text-left text-sm font-medium text-danger-700 transition-colors hover:bg-danger-50"
          >
            <LogOutIcon className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

function initialsOf(name: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  const first = parts[0]
  if (!first) return 'PC'
  if (parts.length === 1) return first.slice(0, 2).toUpperCase()
  const last = parts[parts.length - 1] ?? first
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
}
